import * as React from 'react';
import { Text } from '@cotera/client/app/components/ui/text';

// --- Lib ---
import { getElementOverflowPosition } from '../../utils';
import { isDocumentTitle } from '../turn-into-dropdown/use-turn-into-dropdown';

// --- Tiptap UI ---
import type {
  SuggestionMenuProps,
  SuggestionItem,
  SuggestionMenuRenderProps,
} from '../../tiptap-ui-utils/suggestion-menu';
import { filterSuggestionItems } from '../../tiptap-ui-utils/suggestion-menu';
import { SuggestionMenu } from '../../tiptap-ui-utils/suggestion-menu';

// --- Hooks ---
import type { SlashMenuConfig } from '../slash-dropdown-menu/use-slash-dropdown-menu';
import { useSlashDropdownMenu } from '../slash-dropdown-menu/use-slash-dropdown-menu';

// --- UI Components ---
import { Button } from '@cotera/client/v0/components/ui/button';
import { Separator } from '@cotera/client/v0/components/ui/separator';
import { Card, CardContent } from '@cotera/client/v0/components/ui/card';
import { Icon, IconName } from '@cotera/client/app/components';

type SlashDropdownMenuProps = Omit<
  SuggestionMenuProps,
  'items' | 'children'
> & {
  config?: SlashMenuConfig;
};

export const SlashDropdownMenu = (props: SlashDropdownMenuProps) => {
  const { config, ...restProps } = props;
  const { getSlashMenuItems } = useSlashDropdownMenu(config);

  return (
    <SuggestionMenu
      char="/"
      pluginKey="slashDropdownMenu"
      decorationClass="tiptap-slash-decoration"
      decorationContent="Filter..."
      selector="tiptap-slash-dropdown-menu"
      items={({ query, editor }) =>
        filterSuggestionItems(getSlashMenuItems(editor), query)
      }
      allow={({ editor }) => {
        // Disable slash menu when in document title
        return !isDocumentTitle(editor);
      }}
      {...restProps}
    >
      {(props) => <List {...props} config={config} />}
    </SuggestionMenu>
  );
};

const Item = (props: {
  item: SuggestionItem;
  isSelected: boolean;
  onSelect: () => void;
}) => {
  const { item, isSelected, onSelect } = props;
  const itemRef = React.useRef<HTMLButtonElement>(null);

  React.useEffect(() => {
    const selector = document.querySelector(
      '[data-selector="tiptap-slash-dropdown-menu"]'
    ) as HTMLElement;
    if (itemRef.current == null || !isSelected || selector == null) {
      return;
    }

    const overflow = getElementOverflowPosition(itemRef.current, selector);

    if (overflow === 'top') {
      itemRef.current.scrollIntoView(true);
    } else if (overflow === 'bottom') {
      itemRef.current.scrollIntoView(false);
    }
  }, [isSelected]);

  const BadgeIcon = item.badge;

  return (
    <Button
      ref={itemRef}
      variant="ghost"
      className="w-full justify-start !bg-transparent hover:!bg-gray-100 data-[active-state=on]:!bg-muted data-[active-state=on]:!text-foreground !px-1 !h-fit !py-1.5"
      data-active-state={isSelected ? 'on' : 'off'}
      onClick={onSelect}
    >
      {BadgeIcon != null &&
        (typeof BadgeIcon === 'string' ? (
          <Icon icon={BadgeIcon as IconName} className="tiptap-button-icon" />
        ) : (
          <BadgeIcon className="tiptap-button-icon" />
        ))}
      <div className="tiptap-button-text">{item.title}</div>
    </Button>
  );
};

const List = ({
  items,
  selectedIndex,
  onSelect,
  config,
}: SuggestionMenuRenderProps & { config?: SlashMenuConfig }) => {
  const renderedItems = React.useMemo(() => {
    const rendered: React.ReactElement[] = [];
    const showGroups = config?.showGroups !== false;

    if (!showGroups) {
      items.forEach((item, index) => {
        rendered.push(
          <Item
            key={`item-${index}-${item.title}`}
            item={item}
            isSelected={index === selectedIndex}
            onSelect={() => onSelect(item)}
          />
        );
      });
      return rendered;
    }

    const groups: {
      [groupLabel: string]: { items: SuggestionItem[]; indices: number[] };
    } = {};

    items.forEach((item, index) => {
      const groupLabel = item.group || '';
      if (!groups[groupLabel]) {
        groups[groupLabel] = { items: [], indices: [] };
      }
      groups[groupLabel].items.push(item);
      groups[groupLabel].indices.push(index);
    });

    Object.entries(groups).forEach(([groupLabel, groupData], groupIndex) => {
      if (groupIndex > 0) {
        rendered.push(
          <Separator key={`separator-${groupIndex}`} orientation="horizontal" />
        );
      }

      const groupItems = groupData.items.map((item, itemIndex) => {
        const originalIndex = groupData.indices[itemIndex];
        return (
          <Item
            key={`item-${originalIndex}-${item.title}`}
            item={item}
            isSelected={originalIndex === selectedIndex}
            onSelect={() => onSelect(item)}
          />
        );
      });

      if (groupLabel) {
        rendered.push(
          <div
            key={`group-${groupIndex}-${groupLabel}`}
            className="tiptap-slash-group"
          >
            <Text.Label>{groupLabel}</Text.Label>
            <div className="tiptap-slash-button-group">{groupItems}</div>
          </div>
        );
      } else {
        rendered.push(...groupItems);
      }
    });

    return rendered;
  }, [items, selectedIndex, onSelect, config?.showGroups]);

  if (!renderedItems.length) {
    return null;
  }

  return (
    <Card
      className="tiptap-slash-card !p-2"
      style={{
        maxHeight: 'var(--suggestion-menu-max-height)',
      }}
    >
      <CardContent className="w-[200px] !px-0 flex flex-col gap-1 overflow-y-scroll max-h-[350px]">
        {renderedItems}
      </CardContent>
    </Card>
  );
};
